export interface CityConfig {
  seed: number;
  scale: 'town' | 'city' | 'megacity';
  center: { x: number; z: number };
  radius: number;
  /** Settlement archetype id (default 'grid' = current grid generator). */
  archetype?: string;

  fields: {
    densityFalloff: 'linear' | 'gaussian' | 'stepped';
    resolution: number;
  };

  roads: {
    style: 'grid' | 'radial' | 'organic' | 'mixed';
    arterialSpacing: number;
    collectorSpacing: number;
    localSpacing: number;
    maxGrade: number;
    segmentLength: number;
    snapRadius: number;
    minIntersectionAngle: number;
    arterialHalfWidth: number;
    collectorHalfWidth: number;
    localHalfWidth: number;
    sidewalkWidth: number;
    curbHeight: number;
    crownHeight: number;
  };

  blocks: {
    minArea: number;
    maxAspectRatio: number;
  };

  parcels: {
    subdivisionStyle: 'obb' | 'skeleton' | 'mixed';
    targetArea: { min: number; max: number };
  };

  buildings: {
    maxFloors: number;
    minSetback: number;
    maxCoverage: number;
    minParcelArea: number;
    zoningWeights: { residential: number; commercial: number; industrial: number; park: number };
  };

  terrain: {
    padBevelWidth: number;
    roadFalloff: number;
  };

  nav: {
    enabled: boolean;
    cellSize: number;
    cellHeight: number;
    tileSize: number;
  };

  performance: {
    maxBuildings: number;
    instanceBatching: boolean;
    lodDistances: [number, number];
  };
}

// --- Interfaces for pipeline data ---

export interface RoadNode { id: number; x: number; z: number; }

export interface RoadEdge {
  id: number; from: number; to: number;
  polyline: Float64Array;
  roadClass: 'arterial' | 'collector' | 'local';
  halfWidth: number;
  gradeProfile: Float32Array;
}

export interface RoadGraph {
  nodes: RoadNode[];
  edges: RoadEdge[];
  addNode(x: number, z: number): RoadNode;
  addEdge(fromId: number, toId: number, roadClass: RoadEdge['roadClass'], halfWidth: number): RoadEdge;
  getEdgesFromNode(nodeId: number): RoadEdge[];
  findNearestNode(x: number, z: number, maxDist: number): RoadNode | null;
}

export interface Block {
  id: number;
  polygon: Float64Array;
  edges: number[];
}

export interface Parcel {
  id: number;
  blockId: number;
  polygon: Float64Array;
  frontageEdge: number;
  zone: 'residential' | 'commercial' | 'industrial' | 'park' | 'mixed';
  area: number;
}

export interface BuildingProgram {
  type: 'house' | 'apartment' | 'office' | 'shop' | 'warehouse' | 'factory' | 'skyscraper';
  /** Primary functional classification (set by the building policy; type is derived from it for new archetypes). */
  function?: import('./buildings/BuildingFunction.js').BuildingFunction;
  floors: number;
  roofStyle: 'flat' | 'pitched' | 'hip';
  width: number;
  depth: number;
}

export interface BuildingPad {
  parcelId: number;
  footprint: Float64Array;
  program: BuildingProgram;
  padHeight: number;
  orientation: number;
}

export interface PropPlacement {
  type: 'streetlight' | 'tree' | 'bench' | 'hydrant' | 'trashcan';
  x: number; z: number; y: number;
  rotation: number;
}

export interface CityFields {
  densityAt(x: number, z: number): number;
  zoneAt(x: number, z: number): Parcel['zone'];
  tensorAt(x: number, z: number): { major: [number, number]; minor: [number, number] };
}

export interface CityPlan {
  seed: number;
  config: CityConfig;
  fields: CityFields;
  roads: RoadGraph;
  blocks: Block[];
  parcels: Parcel[];
  buildings: BuildingPad[];
  streetProps: PropPlacement[];
}

// --- Presets ---

const TOWN_OVERRIDES: Partial<CityConfig> = {
  scale: 'town',
  radius: 400,
  fields: { densityFalloff: 'linear', resolution: 32 },
  roads: {
    style: 'organic', arterialSpacing: 200, collectorSpacing: 100, localSpacing: 40,
    maxGrade: 0.08, segmentLength: 20, snapRadius: 15, minIntersectionAngle: 30,
    arterialHalfWidth: 6, collectorHalfWidth: 4, localHalfWidth: 3,
    sidewalkWidth: 2, curbHeight: 0.15, crownHeight: 0.05,
  },
  parcels: { subdivisionStyle: 'obb', targetArea: { min: 200, max: 800 } },
  buildings: {
    maxFloors: 3, minSetback: 6, maxCoverage: 0.4, minParcelArea: 100,
    zoningWeights: { residential: 0.70, commercial: 0.15, industrial: 0.05, park: 0.10 },
  },
  performance: { maxBuildings: 200, instanceBatching: true, lodDistances: [200, 600] },
};

const CITY_OVERRIDES: Partial<CityConfig> = {
  scale: 'city',
  radius: 1200,
  fields: { densityFalloff: 'gaussian', resolution: 64 },
  roads: {
    style: 'mixed', arterialSpacing: 150, collectorSpacing: 75, localSpacing: 30,
    maxGrade: 0.06, segmentLength: 15, snapRadius: 12, minIntersectionAngle: 30,
    arterialHalfWidth: 8, collectorHalfWidth: 5, localHalfWidth: 3,
    sidewalkWidth: 2.5, curbHeight: 0.15, crownHeight: 0.04,
  },
  parcels: { subdivisionStyle: 'mixed', targetArea: { min: 100, max: 600 } },
  buildings: {
    maxFloors: 12, minSetback: 3, maxCoverage: 0.6, minParcelArea: 80,
    zoningWeights: { residential: 0.40, commercial: 0.35, industrial: 0.15, park: 0.10 },
  },
  performance: { maxBuildings: 2000, instanceBatching: true, lodDistances: [200, 600] },
};

const MEGACITY_OVERRIDES: Partial<CityConfig> = {
  scale: 'megacity',
  radius: 3000,
  fields: { densityFalloff: 'stepped', resolution: 128 },
  roads: {
    style: 'mixed', arterialSpacing: 120, collectorSpacing: 60, localSpacing: 25,
    maxGrade: 0.05, segmentLength: 12, snapRadius: 10, minIntersectionAngle: 30,
    arterialHalfWidth: 10, collectorHalfWidth: 6, localHalfWidth: 3.5,
    sidewalkWidth: 3, curbHeight: 0.15, crownHeight: 0.03,
  },
  parcels: { subdivisionStyle: 'mixed', targetArea: { min: 50, max: 400 } },
  buildings: {
    maxFloors: 60, minSetback: 1, maxCoverage: 0.8, minParcelArea: 50,
    zoningWeights: { residential: 0.25, commercial: 0.45, industrial: 0.20, park: 0.10 },
  },
  performance: { maxBuildings: 5000, instanceBatching: true, lodDistances: [300, 800] },
};

const BASE_CONFIG: CityConfig = {
  seed: 42,
  scale: 'city',
  center: { x: 0, z: 0 },
  radius: 1200,
  fields: { densityFalloff: 'gaussian', resolution: 64 },
  roads: {
    style: 'mixed', arterialSpacing: 150, collectorSpacing: 75, localSpacing: 30,
    maxGrade: 0.06, segmentLength: 15, snapRadius: 12, minIntersectionAngle: 30,
    arterialHalfWidth: 8, collectorHalfWidth: 5, localHalfWidth: 3,
    sidewalkWidth: 2.5, curbHeight: 0.15, crownHeight: 0.04,
  },
  blocks: { minArea: 400, maxAspectRatio: 6 },
  parcels: { subdivisionStyle: 'mixed', targetArea: { min: 100, max: 600 } },
  buildings: {
    maxFloors: 12, minSetback: 3, maxCoverage: 0.6, minParcelArea: 80,
    zoningWeights: { residential: 0.40, commercial: 0.35, industrial: 0.15, park: 0.10 },
  },
  terrain: { padBevelWidth: 5, roadFalloff: 8 },
  nav: { enabled: true, cellSize: 0.3, cellHeight: 0.2, tileSize: 64 },
  performance: { maxBuildings: 2000, instanceBatching: true, lodDistances: [200, 600] },
};

function deepMerge(base: CityConfig, overrides: Partial<CityConfig>): CityConfig {
  // First deep-clone base so no nested objects are shared with the original.
  const result = cloneConfig(base) as unknown as Record<string, unknown>;
  const over = overrides as unknown as Record<string, unknown>;
  for (const key of Object.keys(over)) {
    const val = over[key];
    if (val !== undefined && typeof val === 'object' && !Array.isArray(val) && val !== null) {
      result[key] = { ...(result[key] as Record<string, unknown>), ...(val as Record<string, unknown>) };
    } else if (val !== undefined) {
      result[key] = val;
    }
  }
  return result as unknown as CityConfig;
}

/** Deep-clone a CityConfig so nested objects are independent copies.
 *  Handles up to two levels of nesting (e.g. buildings.zoningWeights,
 *  parcels.targetArea). */
function cloneConfig(src: CityConfig): CityConfig {
  const raw = src as unknown as Record<string, unknown>;
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(raw)) {
    const val = raw[key];
    if (Array.isArray(val)) {
      out[key] = [...val];
    } else if (val !== null && typeof val === 'object') {
      // Shallow-clone this level, then deep-clone any nested objects/arrays
      const inner = val as Record<string, unknown>;
      const cloned: Record<string, unknown> = {};
      for (const ik of Object.keys(inner)) {
        const iv = inner[ik];
        if (Array.isArray(iv)) {
          cloned[ik] = [...iv];
        } else if (iv !== null && typeof iv === 'object') {
          cloned[ik] = { ...(iv as Record<string, unknown>) };
        } else {
          cloned[ik] = iv;
        }
      }
      out[key] = cloned;
    } else {
      out[key] = val;
    }
  }
  return out as unknown as CityConfig;
}

export function defaultCityConfig(): CityConfig {
  return cloneConfig(BASE_CONFIG);
}

export function createCityConfig(
  scale: CityConfig['scale'],
  overrides: Partial<CityConfig> = {},
): CityConfig {
  const presetMap = { town: TOWN_OVERRIDES, city: CITY_OVERRIDES, megacity: MEGACITY_OVERRIDES };
  const preset = presetMap[scale] ?? {};
  return deepMerge(deepMerge(BASE_CONFIG, preset), overrides);
}
